/**
 * Module Staff #11 — Gestion clients & entreprises
 * Source : Guide_Admin_Baobab_Connect.pdf · Chapitre 4
 */
const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
const IMG = (name) => `${BACKEND}/api/uploads/docs/html_assets/admin/${name}`;

const moduleStaffClients = {
  id: 'staff-clients',
  population: 'staff',
  order: 11,
  category: 'Admin',
  title: 'Gestion clients & entreprises',
  subtitle: 'Référencer, KYC, contrats-cadres et import CSV en bulk',
  icon: 'Users',
  duration: '20 min',
  level: 'Intermédiaire',
  audience: 'Commerciaux, customer success, admins',
  slides: [
    { type: 'cover', eyebrow: 'Module 11 — Staff', title: 'Gestion clients', subtitle: 'Référentiel client & onboarding maîtrisé', icon: 'Users', duration: '20 min', audience: 'Commercial & CSM', level: 'Intermédiaire' },
    { type: 'intro', title: 'Objectifs', intro: 'La page Clients centralise votre référentiel B2B. Ce module vous guide pour structurer votre base de manière propre et conforme.', objectives: [
      'Créer une fiche entreprise (NCC, RC, DFE, secteur, contact)',
      'Importer un lot de clients via CSV (jusqu\'à 500 lignes)',
      'Lier les utilisateurs (collaborateurs) à une entreprise',
      'Configurer le type de paiement par défaut (Standard / Privilégié / Contrat)',
      'Auditer les statuts actifs/inactifs',
      'Comprendre le lien Clients ↔ Contrats-cadres',
    ]},
    { type: 'screenshot', eyebrow: 'Référentiel B2B', title: 'Gestion des Clients',
      image: IMG('img_admin_clients.png'),
      callouts: [
        { label: 'KPIs synthétiques', text: 'Total / Actifs / Inactifs / Particuliers / Entreprises.', x: 50, y: 25 },
        { label: 'Import CSV', text: 'Bouton dédié pour ajout en masse — template fourni.', x: 30, y: 45 },
        { label: 'Filtres types', text: 'Tous types / Entreprise / Particulier ; secteurs ; paiement.', x: 70, y: 45 },
        { label: 'Liste clients', text: 'Chaque ligne : entreprise + nb membres + secteur + paiement + statut.', x: 50, y: 70 },
      ],
    },
    { type: 'concept', eyebrow: 'KYC', icon: 'BookOpen', title: 'Champs obligatoires d\'une fiche entreprise',
      lede: 'Pour la conformité OHADA, certains champs sont incontournables :',
      bullets: [
        '🏢 Raison sociale + forme juridique (SARL, SA, SAS...)',
        '🆔 NCC (Numéro Compte Contribuable) — unique en Côte d\'Ivoire',
        '📋 RCCM (Registre Commerce & Crédit Mobilier)',
        '📄 DFE (Déclaration Fiscale d\'Existence)',
        '📍 Adresse complète + ville + pays + code postal',
        '👤 Contact principal (nom, fonction, email, téléphone)',
        '🏭 Secteur d\'activité (15 secteurs pré-configurés)',
      ],
      note: 'Une fiche incomplète bloque la création de factures conformes OHADA.',
    },
    { type: 'steps', title: 'Importer un lot de clients via CSV', steps: [
      { title: 'Télécharger le template CSV', detail: 'Bouton "Importer CSV" → "Télécharger template" — fichier avec colonnes pré-définies.' },
      { title: 'Remplir le template', detail: 'Une ligne par client ; champs NCC + Raison sociale obligatoires.' },
      { title: 'Importer le fichier', detail: 'Drag & drop ou bouton parcourir, max 500 lignes par import.' },
      { title: 'Vérifier l\'aperçu', detail: 'Le système détecte les doublons (NCC déjà en base) et les anomalies.' },
      { title: 'Valider l\'import', detail: 'Création en masse + rapport (X créés, Y rejetés avec raison).' },
    ]},
    { type: 'concept', eyebrow: 'Politique tarifaire', icon: 'DollarSign', title: 'Les 3 types de paiement',
      lede: 'Chaque entreprise est étiquetée avec un mode de paiement par défaut :',
      bullets: [
        '🟢 Standard : paiement avant départ ou à 7 jours (PME)',
        '⚡ Privilégié : paiement à 30 jours sans formalité (grands comptes)',
        '🟦 Contrat : conditions négociées dans un contrat-cadre (volume engagé)',
        'Le type définit les écrans accessibles et les automatisations financières.',
      ],
      note: 'Migration de "Standard" vers "Privilégié/Contrat" se fait sur validation CFO uniquement.',
    },
    { type: 'comparison', title: 'Bonnes pratiques de référentiel', do: [
      'Toujours vérifier le NCC avant création (anti-doublon)',
      'Compléter tous les champs OHADA dès la création',
      'Vérifier les pièces KYC (RCCM, DFE) avant activation',
      'Lier systématiquement les contacts aux entreprises',
      'Désactiver les clients dormants (> 12 mois sans opération)',
    ], dont: [
      'Ne créez pas un client sans NCC vérifié (factures rejetées DGI)',
      'Ne dupliquez pas un client existant — chercher avant créer',
      'Ne fusionnez jamais 2 fiches manuellement (passez par le support)',
      'N\'attribuez pas "Privilégié" sans validation CFO',
      'Ne supprimez pas un client avec historique d\'opérations',
    ]},
    { type: 'quiz', title: 'Quiz #1',
      question: 'Un nouveau client souhaite être en "Privilégié" dès la création. Que faire ?',
      options: [
        'Lui accorder directement pour éviter la friction commerciale',
        'Le créer en "Standard" + demander la validation CFO avant migration',
        'Refuser sans discuter',
        'Demander un acompte forfaitaire',
      ],
      answerIndex: 1,
      explanation: 'La création en Standard évite tout risque commercial. Le CFO valide ensuite le passage Privilégié après examen du dossier (KYC + historique).',
    },
    { type: 'quiz', title: 'Quiz #2',
      question: 'Vous importez 200 clients via CSV. L\'aperçu signale 15 doublons NCC. Que faire ?',
      options: [
        'Forcer la création des 200 lignes',
        'Annuler tout l\'import',
        'Corriger les 15 lignes dans le CSV puis ré-importer (le système crée les 185 nouvelles et ignore les doublons)',
        'Créer les 15 doublons manuellement',
      ],
      answerIndex: 2,
      explanation: 'La correction du CSV est la voie propre : l\'import partiel garantit l\'absence de doublon en base tout en avançant sur les 185 nouvelles entrées.',
    },
    { type: 'takeaways', title: 'À retenir', items: [
      'NCC = clé unique d\'identification client. Toujours le vérifier.',
      'Champs OHADA complets dès la création = factures conformes.',
      'Import CSV avec template officiel = gain de temps × 50.',
      '3 types de paiement = 3 niveaux d\'autonomie commerciale.',
      'Désactiver les dormants, jamais supprimer les actifs/historiques.',
    ]},
    { type: 'cta', title: 'Module 11 terminé !', subtitle: 'Vous gérez le référentiel B2B en conformité.',
      actions: [
        { label: 'Ouvrir les clients', href: '/admin/clients' },
        { label: 'Module Contrats', href: '/docs/admin/training' },
        { label: 'Catalogue', href: '/docs/admin/training' },
      ],
    },
  ],
};

export default moduleStaffClients;
